import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { serviceConsumer } from "../../network/ServiceConsumer";
import Loader from "../../Components/Loader/Loader";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import SingleLineTitle from "../../Components/TitleElements/SingleLineTitle";
import moment from "moment";
import DeleteUserModal from "./DeleteUserModal";

const ParentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { parentName } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [parent, setParent] = useState(null);
  const [dancersList, setDancersList] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [editedFname, setEditedFname] = useState("");
  const [editedLname, setEditedLname] = useState("");
  const [editedEmail, setEditedEmail] = useState("");
  const [editedCountry, setEditedCountry] = useState("");

  const [resetLoading, setResetLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [expandedTxn, setExpandedTxn] = useState(null);
  const [txnDetail, setTxnDetail] = useState(null);
  const [txnDetailLoading, setTxnDetailLoading] = useState(false);

  useEffect(() => {
    fetchParentDetails();
  }, [id]);

  const fetchParentDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/parent/${id}`;
      const res = await serviceConsumer("GET", url);
      if (res?.parent) {
        setParent(res.parent);
        setEditedFname(res.parent.fname);
        setEditedLname(res.parent.lname);
        setEditedEmail(res.parent.email);
        setEditedCountry(res.parent.country || "");
      }
      if (res?.dancers) setDancersList(res.dancers);
      if (res?.transactions) setTransactions(res.transactions);
    } catch (err) {
      setError("Failed to load parent details");
      ShowToastMessage("error", "Failed to load parent details");
    } finally {
      setLoading(false);
    }
  };

  const editClickHandler = () => setEditMode(true);

  const cancelHandler = () => {
    setEditMode(false);
    if (parent) {
      setEditedFname(parent.fname);
      setEditedLname(parent.lname);
      setEditedEmail(parent.email);
      setEditedCountry(parent.country || "");
    }
  };

  const saveHandler = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/parent/${id}`;
      const payload = {
        fname: editedFname,
        lname: editedLname,
        email: editedEmail,
        country: editedCountry,
      };
      await serviceConsumer("PUT", url, payload);
      ShowToastMessage("success", "Parent updated successfully");
      setEditMode(false);
      setParent({ ...parent, ...payload });
    } catch (err) {
      ShowToastMessage("error", "Something went wrong while saving");
    } finally {
      setLoading(false);
    }
  };

  const initiatePasswordReset = async () => {
    try {
      setResetLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/parent/${id}/reset-password`;
      const res = await serviceConsumer("POST", url);
      ShowToastMessage("success", res.message || "Password reset email sent");
    } catch (err) {
      ShowToastMessage("error", "Failed to send password reset email");
    } finally {
      setResetLoading(false);
    }
  };

  const toggleTxnDetail = async (txn) => {
    if (expandedTxn === txn._id) {
      setExpandedTxn(null);
      setTxnDetail(null);
      return;
    }
    try {
      setExpandedTxn(txn._id);
      setTxnDetailLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/transaction/${txn._id}`;
      const res = await serviceConsumer("GET", url);
      setTxnDetail(res);
    } catch (err) {
      ShowToastMessage("error", "Failed to load transaction details");
      setExpandedTxn(null);
    } finally {
      setTxnDetailLoading(false);
    }
  };

  const backHandler = () => navigate("/users");

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!parent) return <p className="text-center">No data available</p>;

  return (
    <>
      <SingleLineTitle
        titleName={parentName || `${parent.fname} ${parent.lname}`}
        onBackClick={backHandler}
      />

      <div className="db-body">
        {/* Header with edit/save/cancel */}
        <div className="d-flex align-items-center justify-content-between mb-4 pt-3">
          <h3>{editedFname} {editedLname}</h3>
          <div className="d-flex gap-3">
            {!editMode ? (
              <EditIcon
                style={{ cursor: "pointer", color: "#68ad3f" }}
                titleAccess="Edit Parent"
                onClick={editClickHandler}
              />
            ) : (
              <>
                <SaveIcon
                  style={{ cursor: "pointer", color: "#68ad3f" }}
                  titleAccess="Save Changes"
                  onClick={saveHandler}
                />
                <CloseIcon
                  style={{ cursor: "pointer", color: "red" }}
                  titleAccess="Cancel"
                  onClick={cancelHandler}
                />
              </>
            )}
          </div>
        </div>

        {/* Parent Info */}
        <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "20px", border: "1px solid #e8e8e8" }}>
          {!editMode ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>First Name</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{parent.fname}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Last Name</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{parent.lname}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Email</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{parent.email}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Region</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{parent.country || "N/A"}</div>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>First Name</label>
                <input type="text" className="dashboard-input" value={editedFname} onChange={(e) => setEditedFname(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>Last Name</label>
                <input type="text" className="dashboard-input" value={editedLname} onChange={(e) => setEditedLname(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>Email</label>
                <input type="email" className="dashboard-input" value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>Region</label>
                <input type="text" className="dashboard-input" value={editedCountry} onChange={(e) => setEditedCountry(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
            </div>
          )}
        </div>

        {/* Password Reset + Delete */}
        <div className="mt-3 mb-4 d-flex gap-3">
          <button
            style={{ fontSize: "13px", padding: "6px 14px", border: "1px solid #68ad3f", borderRadius: "4px", backgroundColor: "transparent", color: "#68ad3f", cursor: "pointer", fontWeight: 500 }}
            onClick={initiatePasswordReset}
            disabled={resetLoading}
          >
            {resetLoading ? "Sending..." : "Send Password Reset Email"}
          </button>
          <button
            style={{ fontSize: "13px", padding: "6px 14px", border: "1px solid #b00020", borderRadius: "4px", backgroundColor: "transparent", color: "#b00020", cursor: "pointer", fontWeight: 500 }}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Account
          </button>
        </div>

        {/* Dancers */}
        <h4 className="mt-5">Dancers</h4>
        <hr />
        <div className="table-responsive">
          <table className="table my_table">
            <tbody>
              <tr className="header-row">
                <td className="p-25">Name</td>
                <td className="p-25-5">School</td>
                <td className="p-25-5">DOB</td>
              </tr>
              {dancersList.length > 0 ? (
                dancersList.map((dancer, i) => (
                  <tr key={dancer._id || i} style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => navigate(`/dancers/details/${dancer._id}`, { state: { dancerName: `${dancer.fname} ${dancer.lname}` } })}>
                    <td style={{ color: "#68ad3f" }}>{dancer.fname} {dancer.lname}</td>
                    <td>{dancer.school || "N/A"}</td>
                    <td>{dancer.birthday || "N/A"}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="3" className="text-center">No dancers found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Transactions */}
        <h4 className="mt-5">Transactions (Receipts)</h4>
        <hr />
        <div className="table-responsive" style={{ marginBottom: "50px" }}>
          <table className="table my_table">
            <tbody>
              <tr className="header-row">
                <td className="p-25">Receipt #</td>
                <td className="p-25-5">Event</td>
                <td className="p-25-5">Dancer</td>
                <td className="p-25-5">Amount</td>
                <td className="p-25-5">Type</td>
                <td className="p-25-5">Date</td>
              </tr>
              {transactions.length > 0 ? (
                transactions.map((txn, i) => {
                  // Receipts can now cover multiple dancers (one Stripe payment
                  // = one receipt). Show first dancer's name + a "+N more"
                  // suffix when dancerCount > 1 so the list stays compact.
                  const dancerLabel = txn.dancerData
                    ? `${txn.dancerData.fname} ${txn.dancerData.lname}${
                        txn.dancerCount > 1 ? ` (+${txn.dancerCount - 1} more)` : ""
                      }`
                    : "N/A";
                  // Edits charge only a delta — `paidAmount` is the actual
                  // Stripe charge; `totalPayableAmount` is the new full
                  // dancer total. For originals they're equal.
                  const amount = txn.paidAmount || txn.totalPayableAmount || 0;
                  // Type column distinguishes original vs. edit-add receipts.
                  const typeLabel = txn.isEdit
                    ? `${txn.event_type || "feis"} (edit)`
                    : (txn.event_type || "feis");
                  return (
                    <React.Fragment key={txn._id || i}>
                      <tr
                        style={{ fontSize: "14px", cursor: "pointer", background: expandedTxn === txn._id ? "#f5f9f3" : "#fff" }}
                        onClick={() => toggleTxnDetail(txn)}
                      >
                        <td style={{ color: "#68ad3f" }}>{txn.receipt ? String(txn.receipt).padStart(6, "0") : "N/A"}</td>
                        <td>{txn.eventData?.name || txn.eventData?.feis_name || "N/A"}</td>
                        <td>{dancerLabel}</td>
                        <td>${amount?.toFixed(2) || "0.00"}</td>
                        <td>{typeLabel}</td>
                        <td>{txn.createdAt ? moment(txn.createdAt).format("MMM D, YYYY") : "N/A"}</td>
                      </tr>
                      {expandedTxn === txn._id && (
                        <tr>
                          <td colSpan="6" style={{ padding: 0, border: "none" }}>
                            <TransactionDetail
                              loading={txnDetailLoading}
                              detail={txnDetail}
                              eventType={txn.event_type}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr><td colSpan="6" className="text-center">No transactions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteUserModal
          userId={id}
          userType="user"
          displayName={`${parent.fname} ${parent.lname}`}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => {
            setShowDeleteModal(false);
            navigate("/users");
          }}
        />
      )}
    </>
  );
};

const TransactionDetail = ({ loading, detail, eventType }) => {
  if (loading) return <div style={{ padding: "16px", textAlign: "center" }}>Loading...</div>;
  if (!detail) return null;

  const { transaction, dancers: dancerSections, eventData } = detail;
  const isGrade = eventType === "grade";
  const isEdit = !!transaction.isEdit;
  const entriesSubtotal = transaction.allLevelAmount || 0;
  const familyFee = transaction.fee || 0;
  // For the customer-facing service fee, sum dancerServiceAmount across all
  // dancers in this payment. Fall back to whole-payment serviceAmount if the
  // legacy single-dancer code path is in play.
  const dancerServiceTotal = Array.isArray(dancerSections)
    ? dancerSections.reduce((sum, d) => sum + (d.dancerServiceAmount || 0), 0)
    : (transaction.dancerServiceAmount || 0);
  // Total Paid:
  //  - Edits: `paidAmount` is the Stripe delta actually charged this time.
  //  - Originals: paidAmount === totalPayableAmount, so either works;
  //    prefer paidAmount because it's the field stored by the Stripe
  //    intent itself.
  const totalPaid = isEdit
    ? transaction.paidAmount || 0
    : transaction.paidAmount || transaction.totalPayableAmount || 0;
  const computedService = totalPaid - entriesSubtotal - familyFee;
  const serviceFee = computedService > 0 ? computedService : (dancerServiceTotal || transaction.serviceAmount || 0);

  const renderItem = (item, idx, last) => (
    <div
      key={item._id || idx}
      style={{
        display: "flex", justifyContent: "space-between", padding: "8px 12px",
        borderBottom: !last ? "1px solid #f0f0f0" : "none",
        fontSize: "13px",
      }}
    >
      <span>
        {isGrade
          ? item.grade_name
          : `${item.code} - ${item.description}${item.group ? ` (${item.group})` : ""}`
        }
      </span>
      <span style={{ fontWeight: 500 }}>
        ${isGrade
          ? (item.grade_fee?.toFixed(2) || "0.00")
          : (item.price?.toFixed(2) || "0.00")
        }
      </span>
    </div>
  );

  return (
    <div style={{ background: "#fafafa", border: "1px solid #e8e8e8", borderRadius: "6px", margin: "8px 12px 16px", padding: "20px" }}>
      {/* Receipt Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid #e0e0e0" }}>
        <div>
          <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Receipt
            {isEdit && (
              <span style={{
                marginLeft: "8px", padding: "2px 6px", background: "#fff3cd",
                color: "#856404", borderRadius: "3px", fontSize: "10px", fontWeight: 600,
              }}>
                Add Entries
              </span>
            )}
          </div>
          <div style={{ fontSize: "16px", fontWeight: 600 }}>#{String(transaction.receipt).padStart(6, "0")}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date</div>
          <div style={{ fontSize: "14px" }}>{moment(transaction.createdAt).format("MMM D, YYYY")}</div>
        </div>
      </div>

      {/* Event */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "11px", color: "#888", marginBottom: "2px" }}>Event</div>
        <div style={{ fontSize: "14px", fontWeight: 500 }}>{eventData?.name || "N/A"}</div>
      </div>

      {/* Per-dancer sections — one block per dancer in the payment */}
      {Array.isArray(dancerSections) && dancerSections.length > 0 ? (
        dancerSections.map((section, secIdx) => (
          <div key={section._id || secIdx} style={{ marginBottom: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#333" }}>
                {section.dancer ? `${section.dancer.fname} ${section.dancer.lname}` : "Dancer"}
              </div>
              <div style={{ fontSize: "11px", color: "#888" }}>
                Receipt #{String(section.receipt).padStart(6, "0")}
              </div>
            </div>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#555", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
              {isEdit
                ? (isGrade ? "Added Grade Exams" : "Added Entries")
                : (isGrade ? "Grade Exams" : "Competitions")}
            </div>
            <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "4px" }}>
              {section.items && section.items.length > 0 ? (
                section.items.map((item, idx) =>
                  renderItem(item, idx, idx === section.items.length - 1)
                )
              ) : (
                <div style={{ padding: "8px 12px", fontSize: "13px", color: "#999" }}>No entries found</div>
              )}
            </div>
          </div>
        ))
      ) : (
        <div style={{ padding: "8px 12px", fontSize: "13px", color: "#999", marginBottom: "12px" }}>No dancers found</div>
      )}

      {/* Fee Breakdown — per-payment (not per-dancer), so familyFee + service appear once.
          Edits skip Family Fee entirely (collected with the original payment). */}
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: "4px", marginBottom: "12px" }}>
        {entriesSubtotal > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", fontSize: "13px", borderBottom: "1px solid #f0f0f0" }}>
            <span>
              {isEdit
                ? "Subtotal (added entries)"
                : `Subtotal (entries${dancerSections && dancerSections.length > 1 ? `, ${dancerSections.length} dancers` : ""})`}
            </span>
            <span>${entriesSubtotal.toFixed(2)}</span>
          </div>
        )}
        {!isEdit && familyFee > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", fontSize: "13px", borderBottom: "1px solid #f0f0f0" }}>
            <span>Family Fee</span>
            <span>${familyFee.toFixed(2)}</span>
          </div>
        )}
        {serviceFee > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", fontSize: "13px", borderBottom: "1px solid #f0f0f0" }}>
            <span>Service & Processing Fee</span>
            <span>${serviceFee.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Total */}
      <div style={{
        display: "flex", justifyContent: "space-between", padding: "10px 12px",
        background: "#68ad3f", borderRadius: "4px", color: "#fff", fontWeight: 600, fontSize: "15px",
      }}>
        <span>{isEdit ? "Charged (this edit)" : "Total Paid"}</span>
        <span>${totalPaid.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default ParentDetails;
